import { Note } from '@/store/notesStore';
import { renderMarkdownSafe } from '@/utils/md-to-html/md-to-html';

const MAX_LENGTH = 150;

/**
 * Strip all HTML tags from a string using the browser DOM.
 * This correctly handles custom elements rendered by markdown-it plugins
 * (e.g. secret fields become "••• label" after textContent extraction).
 */
function htmlToPlainText(html: string): string {
  const el = document.createElement('div');
  el.innerHTML = html;
  return el.textContent ?? '';
}

/**
 * Truncate a plain-text string to MAX_LENGTH, appending "…" if cut.
 */
function truncate(text: string): string {
  const trimmed = text.replace(/\s+/g, ' ').trim();
  return trimmed.length > MAX_LENGTH
    ? trimmed.slice(0, MAX_LENGTH) + '…'
    : trimmed;
}

/**
 * Extract a plain-text excerpt from a note's content, respecting its content type.
 *
 * - MDX  : rendered to HTML (so custom directives like :secret[...] are expanded),
 *           then stripped to plain text via DOM.
 * - TXT  : plain text slice.
 * - encrypted: returns a fixed placeholder so the list row is still meaningful.
 */
export function extractNoteExcerpt(note: Note): string {
  if (note.encrypted) {
    return '[Encrypted]';
  }

  const contentType = note.content_type ?? 'MDX';

  if (contentType === 'TXT') {
    return truncate(note.content);
  }

  // MDX (and any unknown type) — render through the markdown pipeline first
  const html = renderMarkdownSafe(note.content ?? '');
  const plain = htmlToPlainText(html);
  return truncate(plain);
}

/**
 * Return the best single-line label for a note in list view.
 * Prefers the note title; falls back to a content excerpt.
 */
export function getNoteDisplayText(note: Note): string {
  if (note.title && note.title.trim().length > 0) {
    return note.title.trim();
  }
  return extractNoteExcerpt(note);
}
